import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Header from "./components/Header";
import Login from "./pages/Login";
import Register from "./pages/Register";
import SetPassword from "./pages/SetPassword";
import ForgotPassword from "./pages/ForgotPassword";
import Dashboard from "./pages/Dashboard";
import Handbook from "./pages/Handbook";
import FormsHub from "./pages/FormsHub";
import FormOpen from "./pages/FormOpen";
import Policies from "./pages/Policies";
import Account from "./pages/Account";
import { useFamilyAuth } from "./store/familyAuthStore";
import { familyService } from "./services/familyService";

function Protected({ children }) {
  const token = useFamilyAuth((s) => s.token);
  const location = useLocation();
  if (!token) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

function PortalShell({ children }) {
  return (
    <div className="portal">
      <Header />
      <main className="portal-main scroll-area">{children}</main>
    </div>
  );
}

export default function App() {
  const token = useFamilyAuth((s) => s.token);
  const setFamily = useFamilyAuth((s) => s.setFamily);

  // Global form validation: red borders on empty required fields at submit;
  // borders clear as the user fixes each field.
  useEffect(() => {
    const fieldSelector = "input, select, textarea";
    const errorCls = (el) => (el instanceof HTMLSelectElement ? "select--error" : "input--error");

    const onSubmitCapture = (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      form.classList.add("was-validated");
      form.querySelectorAll(fieldSelector).forEach((el) => {
        if (!el.willValidate) return;
        if (!el.checkValidity()) {
          el.classList.add(errorCls(el));
        } else {
          el.classList.remove(errorCls(el));
        }
      });
    };

    const onInputCapture = (e) => {
      const el = e.target;
      if (
        !(el instanceof HTMLInputElement) &&
        !(el instanceof HTMLSelectElement) &&
        !(el instanceof HTMLTextAreaElement)
      ) {
        return;
      }
      if (el.validity.customError) {
        el.setCustomValidity("");
      }
      const cls = errorCls(el);
      if (el.classList.contains(cls) && el.checkValidity()) {
        el.classList.remove(cls);
      }
    };

    document.addEventListener("submit", onSubmitCapture, true);
    document.addEventListener("input", onInputCapture, true);
    document.addEventListener("change", onInputCapture, true);
    return () => {
      document.removeEventListener("submit", onSubmitCapture, true);
      document.removeEventListener("input", onInputCapture, true);
      document.removeEventListener("change", onInputCapture, true);
    };
  }, []);

  // Refresh family + centre details on load so the header/dashboard stay current.
  useEffect(() => {
    if (!token) return;
    familyService.verify()
      .then((r) => { if (r?.success && r?.data?.family) setFamily(r.data.family); })
      .catch(() => {});
  }, [token, setFamily]);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot" element={<ForgotPassword />} />
        <Route path="/set-password" element={<SetPassword />} />

        <Route path="/" element={<Protected><PortalShell><Dashboard /></PortalShell></Protected>} />
        <Route path="/dashboard" element={<Protected><PortalShell><Dashboard /></PortalShell></Protected>} />
        <Route path="/handbook" element={<Protected><PortalShell><Handbook /></PortalShell></Protected>} />
        <Route path="/forms" element={<Protected><PortalShell><FormsHub /></PortalShell></Protected>} />
        <Route path="/forms/:code" element={<Protected><PortalShell><FormOpen /></PortalShell></Protected>} />
        <Route path="/policies" element={<Protected><PortalShell><Policies /></PortalShell></Protected>} />
        <Route path="/account" element={<Protected><PortalShell><Account /></PortalShell></Protected>} />
        <Route path="/account/:tab" element={<Protected><PortalShell><Account /></PortalShell></Protected>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
